import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Toast } from '../components/common';
import dnaLogo from '../img/DNA LOGO 2.png';
import './Login.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'error' as 'success' | 'error'
  });

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Determinar si el input parece ser un teléfono
  const isPhoneNumber = (input: string): boolean => {
    if (!input) return false;
    if (input.includes('@')) return false;
    const digits = input.replace(/\D/g, '');
    return digits.length >= 10;
  };

  const validateForm = (): boolean => {
    const newErrors = { email: '', password: '' };
    let isValid = true;

    if (!formData.email) {
      newErrors.email = 'El email, teléfono o username es requerido';
      isValid = false;
    } else {
      // Validar según el tipo de identificador
      const input = formData.email.trim();
      // Email: tiene @ en medio (no al inicio) y formato válido
      const isUsernameWithAt = input.startsWith('@');
      const isEmail = !isUsernameWithAt && input.includes('@') && /\S+@\S+\.\S+/.test(input);
      const isPhone = isPhoneNumber(input);

      if (isUsernameWithAt) {
        // Username con @ - solo validar que tenga al menos 2 caracteres después del @
        if (input.length < 2) {
          newErrors.email = 'Username inválido';
          isValid = false;
        }
      } else if (input.includes('@')) {
        // Tiene @ pero no al inicio - debe ser email válido
        if (!isEmail) {
          newErrors.email = 'Email inválido';
          isValid = false;
        }
      } else if (isPhone) {
        // Validar teléfono
        const digits = input.replace(/\D/g, '');
        if (digits.length < 10) {
          newErrors.email = 'El teléfono debe tener al menos 10 dígitos';
          isValid = false;
        }
      }
      // Si no es email, teléfono, ni username con @, es username sin @ - no requiere validación especial
    }

    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
      isValid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpiar error al escribir
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const result = await login({
        email: formData.email,
        password: formData.password
      });

      if (result.success) {
        setToast({
          isVisible: true,
          message: 'Inicio de sesión exitoso',
          type: 'success'
        });
        navigate('/dashboard');
      } else {
        setToast({
          isVisible: true,
          message: result.message || 'Error de autenticación',
          type: 'error'
        });
      }
    } catch (error: any) {
      setToast({
        isVisible: true,
        message: error.message || 'Error de conexión',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="login-loading">
        <div className="login-spinner"></div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      {/* Fondo con efecto */}
      <div className="login-background">
        <div className="login-bg-shape shape-1"></div>
        <div className="login-bg-shape shape-2"></div>
        <div className="login-bg-shape shape-3"></div>
      </div>

      {/* Card de Login */}
      <div className="login-container">
        <div className="login-card">
          {/* Header */}
          <div className="login-header">
            <div className="login-logo">
              <img src={dnaLogo} alt="DNA Evoluciona" className="logo-image" />
            </div>
            <h1 className="login-title">Estado de cuenta clientes</h1>
          </div>

          {/* Formulario */}
          <form className="login-form" onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="text"
              name="email"
              placeholder="email, teléfono o @username"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              leftIcon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              }
              autoComplete="username"
            />

            <Input
              label="Contraseña"
              type="password"
              name="password"
              placeholder="Ingresa tu contraseña"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              leftIcon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              }
              autoComplete="current-password"
            />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={isSubmitting}
            >
              Iniciar sesión
            </Button>
          </form>

        </div>

        {/* Info extra */}
        <div className="login-info">
          <p>¿Necesitas ayuda? Contacta al administrador</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
